import React, { useEffect, useState } from 'react';
import { Activity, Mail, MousePointerClick, Reply, CalendarCheck, TrendingUp } from 'lucide-react';
import { authFetch } from '../../lib/auth';
import { Card, Fade } from './os2ui';

/* Smart CRM widgets: Lead Health Score, Contact Timeline, Campaign Intelligence */

const GRADE_STYLE = {
  hot: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warm: 'bg-amber-50 text-amber-700 border-amber-200',
  cool: 'bg-sky-50 text-sky-700 border-sky-200',
  cold: 'bg-gray-100 text-gray-500 border-gray-200',
  at_risk: 'bg-red-50 text-red-600 border-red-200',
};
const GRADE_LABEL = {
  hot: ['Hot', 'Caliente'], warm: ['Warm', 'Tibio'], cool: ['Cool', 'Frío'],
  cold: ['Cold', 'Inactivo'], at_risk: ['At risk', 'En riesgo'],
};

export const HealthChip = ({ score, grade, isEs }) => {
  if (score == null) return <span className="text-gray-300 text-[12px]">—</span>;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11.5px] font-bold tabular-nums ${GRADE_STYLE[grade] || GRADE_STYLE.cold}`}
      data-testid="health-chip" title={GRADE_LABEL[grade]?.[isEs ? 1 : 0]}>
      <Activity className="w-3 h-3" /> {score}
    </span>
  );
};

export function useHealthScores(emails) {
  const [scores, setScores] = useState({});
  useEffect(() => {
    if (!emails?.length) return;
    authFetch('/crm/health-scores', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails: emails.slice(0, 300) }),
    }).then((r) => r.json()).then((j) => setScores(j.scores || {})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(emails?.slice(0, 300))]);
  return scores;
}

const KIND_ICON = (kind) => {
  if (kind.startsWith('email_click') || kind === 'email_clicked') return '🖱️';
  if (kind.startsWith('email_open') || kind.startsWith('email_unique') || kind.startsWith('email_proxy')) return '👀';
  if (kind.startsWith('email_delivered')) return '📬';
  if (kind.includes('bounce') || kind.includes('blocked') || kind.includes('spam')) return '⚠️';
  if (kind.includes('unsub')) return '🚪';
  if (kind === 'reply') return '💬';
  if (kind === 'payment') return '💳';
  if (kind === 'crm_sync') return '🔁';
  if (kind === 'crm_note') return '📝';
  if (kind === 'crm_call_outcome') return '📞';
  if (kind.startsWith('lifecycle') || kind.startsWith('crm_lifecycle')) return '📈';
  if (kind.startsWith('lead')) return '⭐';
  return '•';
};

export function ContactTimeline({ email, isEs }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [kind, setKind] = useState('note');
  const [saving, setSaving] = useState(false);
  const load = () => {
    authFetch(`/crm/contact/${encodeURIComponent(email)}/timeline`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setData).catch((e) => setErr(String(e.message)));
  };
  useEffect(() => { setData(null); setErr(''); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [email]);
  const saveNote = async () => {
    if (!note.trim() || saving) return;
    setSaving(true);
    try {
      const r = await authFetch(`/crm/contact/${encodeURIComponent(email)}/note`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.trim(), kind }),
      });
      if (r.ok) { setNote(''); load(); }
    } finally { setSaving(false); }
  };
  if (err) return <p className="mt-4 text-[12px] text-red-500">{err}</p>;
  if (!data) return <p className="mt-4 text-[12px] text-gray-400 animate-pulse">{isEs ? 'Cargando timeline…' : 'Loading timeline…'}</p>;
  const h = data.health || {};
  return (
    <div className="mt-5" data-testid="contact-timeline">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] tracking-wider uppercase font-semibold text-gray-500">{isEs ? 'Salud del lead' : 'Lead health'}</p>
          <HealthChip score={h.score} grade={h.grade} isEs={isEs} />
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-gray-500">
          <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {h.opens ?? 0} {isEs ? 'aperturas' : 'opens'}</span>
          <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3.5 h-3.5" /> {h.clicks ?? 0} clicks</span>
          <span className="inline-flex items-center gap-1"><Reply className="w-3.5 h-3.5" /> {h.replies ?? 0} {isEs ? 'respuestas' : 'replies'}</span>
          {h.days_since_activity != null && <span>{isEs ? 'últ. actividad hace' : 'last activity'} {h.days_since_activity}d</span>}
          {h.penalty > 0 && <span className="text-red-500">-{h.penalty} {isEs ? 'penalización' : 'penalty'}</span>}
        </div>
        {h.next_action && (
          <p className="mt-2.5 rounded-lg bg-[#FFF7ED] border border-orange-100 px-2.5 py-1.5 text-[12px] text-gray-700" data-testid="timeline-next-action">
            <span className="font-bold text-[#d8651a]">✨ {isEs ? 'Siguiente paso' : 'Next step'}:</span> {h.next_action[isEs ? 'es' : 'en']}
          </p>
        )}
      </div>
      <div className="mt-3 flex items-center gap-1.5" data-testid="timeline-note-composer">
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="note-kind-select"
          className="h-8 rounded-lg border border-gray-200 bg-white px-1.5 text-[11.5px] text-gray-600 focus:outline-none">
          <option value="note">{isEs ? 'Nota' : 'Note'}</option>
          <option value="call_outcome">{isEs ? 'Llamada' : 'Call'}</option>
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && saveNote()}
          placeholder={isEs ? 'Añadir nota o resultado de llamada…' : 'Add a note or call outcome…'}
          data-testid="note-input"
          className="h-8 flex-1 rounded-lg border border-gray-200 bg-white px-2.5 text-[12px] text-gray-800 placeholder:text-gray-300 focus:outline-none focus:border-[#F77F11]" />
        <button onClick={saveNote} disabled={!note.trim() || saving} data-testid="note-save-btn"
          className="h-8 rounded-lg bg-[#F77F11] px-3 text-[11.5px] font-bold text-white disabled:opacity-40">
          {saving ? '…' : (isEs ? 'Guardar' : 'Save')}
        </button>
      </div>
      <p className="mt-4 mb-2 text-[11px] tracking-wider uppercase font-semibold text-gray-500">
        Timeline <span className="text-gray-300 normal-case">· {data.count} {isEs ? 'interacciones' : 'interactions'}</span>
      </p>
      <div className="max-h-72 overflow-y-auto pr-1 space-y-0.5" data-testid="timeline-list">
        {data.timeline.map((t, i) => (
          <div key={i} className="flex items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50">
            <span className="text-[13px] leading-5 w-5 text-center shrink-0">{KIND_ICON(t.kind)}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] text-gray-800 font-medium truncate">{isEs ? t.title_es : t.title_en}
                <span className="ml-1.5 text-[10.5px] text-gray-400 font-normal uppercase">{t.source}</span>
              </p>
              {t.detail && <p className="text-[11.5px] text-gray-400 truncate">{t.detail}</p>}
            </div>
            <span className="text-[10.5px] text-gray-400 tabular-nums shrink-0">{String(t.ts).slice(0, 16).replace('T', ' ')}</span>
          </div>
        ))}
        {!data.timeline.length && <p className="text-[12px] text-gray-400 py-4 text-center">{isEs ? 'Sin interacciones registradas aún' : 'No interactions recorded yet'}</p>}
      </div>
    </div>
  );
}

const Rate = ({ v, warn }) => (
  <span className={`tabular-nums font-semibold ${v > 0 ? (warn ? 'text-amber-600' : 'text-gray-900') : 'text-gray-300'}`}>{v}%</span>
);

export function CampaignIntelligence({ isEs }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    authFetch('/crm/campaigns/intelligence?days=90')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setD).catch((e) => setErr(String(e.message)));
  }, []);
  if (err) return null;
  if (!d) return null;
  return (
    <Fade delay={0.1}>
      <Card className="mt-6 overflow-hidden" data-testid="campaign-intelligence">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
            <TrendingUp className="w-4 h-4 text-[#F77F11]" /> Campaign Intelligence
            <span className="text-gray-300 normal-case font-normal">· {isEs ? 'últimos' : 'last'} {d.window_days}d · {isEs ? 'nativo KITXENS' : 'KITXENS native'}</span>
          </p>
          <p className="text-[11.5px] text-gray-400">
            {d.totals.campaigns} {isEs ? 'campañas' : 'campaigns'} · {isEs ? 'apertura media' : 'avg open'} {d.totals.avg_open_rate}% · {d.totals.total_conversions} {isEs ? 'conversiones' : 'conversions'}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]" data-testid="campaign-intel-table">
            <thead><tr className="text-left text-[10.5px] uppercase tracking-wider text-gray-400">
              {[isEs ? 'Campaña' : 'Campaign', isEs ? 'Audiencia' : 'Audience', isEs ? 'Apertura' : 'Open',
                'Click', isEs ? 'Respuesta' : 'Reply', isEs ? 'Reunión' : 'Meeting',
                isEs ? 'Conversión' : 'Conversion', isEs ? 'Bajas' : 'Unsub'].map((h) => (
                <th key={h} className="px-4 py-2 font-semibold">{h}</th>))}
            </tr></thead>
            <tbody>
              {d.campaigns.map((c) => (
                <tr key={c.campaign} className="border-t border-gray-100 hover:bg-orange-50/30" data-testid={`campaign-row-${c.campaign}`}>
                  <td className="px-4 py-2 font-semibold text-gray-800 max-w-[220px] truncate">{c.campaign}</td>
                  <td className="px-4 py-2 tabular-nums">{c.audience}</td>
                  <td className="px-4 py-2"><Rate v={c.open_rate} /> <span className="text-gray-300">({c.opened})</span></td>
                  <td className="px-4 py-2"><Rate v={c.click_rate} /> <span className="text-gray-300">({c.clicked})</span></td>
                  <td className="px-4 py-2"><Rate v={c.reply_rate} /> <span className="text-gray-300">({c.replies})</span></td>
                  <td className="px-4 py-2"><Rate v={c.meeting_rate} /> <span className="text-gray-300">({c.meetings})</span></td>
                  <td className="px-4 py-2"><Rate v={c.conversion_rate} /> <span className="text-gray-300">({c.conversions})</span></td>
                  <td className="px-4 py-2"><Rate v={c.unsubscribed} warn /></td>
                </tr>
              ))}
              {!d.campaigns.length && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400" data-testid="campaign-intel-empty">
                  {isEs ? 'Aún sin eventos de campaña — los datos aparecerán automáticamente con la actividad de Brevo.' : 'No campaign events yet — data will appear automatically with Brevo activity.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {d.segments.length > 0 && (
          <div className="border-t border-gray-100 px-4 py-3">
            <p className="text-[10.5px] uppercase tracking-wider text-gray-400 font-semibold mb-2 inline-flex items-center gap-1.5">
              <CalendarCheck className="w-3.5 h-3.5" /> {isEs ? 'Por segmento (CRM Sync)' : 'By segment (CRM Sync)'}
            </p>
            <div className="flex flex-wrap gap-2">
              {d.segments.slice(0, 8).map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[11.5px] text-gray-600" data-testid={`segment-chip-${i}`}>
                  <b className="text-gray-800">{s.campaign}</b>{s.segment !== '—' && <> · {s.segment}</>} · {s.synced} leads · {s.open_rate}% open · {s.conversions} conv.
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>
    </Fade>
  );
}
